import React, { useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Table, Button } from "reactstrap";
import {
  Loading, WarningAlert, PaginationControls, ItemsPerPageSelector, SortableHeaderCell, EntityRow, ConfirmationModal,
  NotificationToast, SimpleFormModal, SearchBar, InformationalModal
} from "../components";
import useSortableData from "../hooks/useSortableData";
import { useEntity } from "../hooks/useEntity";
import { usersApi } from "../api/entitiesApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { userFields, userDefaultValues } from "../utils/entityFormFields";
import { Link } from "react-router-dom";
import { timezoneOptions } from '../utils/entityFormFields';

// styles
import "../App.css";

export function UsersComponent() {
  const { data: users, isLoading, error, setData: setUsers, setError } = useEntity(usersApi);
  const { items: sortedUsers, requestSort, sortConfig } = useSortableData(users, { key: '', direction: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(50);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [deleteData, setDeleteData] = useState({ open: false, userId: null });
  const [notification, setNotification] = useState({ visible: false, title: "", message: "" });
  const [searchTerm, setSearchTerm] = useState("");
  const [errorMessage, setErrorMessage] = useState({ open: false, title: "", message: "" });


  const totalPages = Math.ceil(users.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;

  const itemsPerPage = [50, 100];
  const filteredUsers = sortedUsers.filter(user => {
    if (!searchTerm) return true;
    const lowerTerm = searchTerm.toLowerCase();
    return (
      (user.idp_id && user.idp_id.toString().toLowerCase().includes(lowerTerm)) ||
      (user.id && user.id.toString().toLowerCase().includes(lowerTerm)) ||
      (user.customer_id && user.customer_id.toString().toLowerCase().includes(lowerTerm)) ||
      (user.email && user.email.toString().toLowerCase().includes(lowerTerm)) ||
      (user.language && user.language.toString().toLowerCase().includes(lowerTerm)) ||
      (user.timezone && user.timezone.toString().toLowerCase().includes(lowerTerm)) 
    );
  });
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  function toggleDropdown() { setDropdownOpen(prevState => !prevState); }
  function handlePageChange(pageNumber) { setCurrentPage(pageNumber); }
  function handleDropdownChange(number) {
    setUsersPerPage(number);
    setCurrentPage(1);
  };

  function handleEdit(user) {
    setCurrentForm({ mode: "edit", data: user });
  };

  function handleAdd() {
    setCurrentForm({ mode: "add", data: { customer_id: "" } });
  }

  async function handleSaveForm(formData) {
    try {
      if (currentForm.mode === "edit") {
        await usersApi.updateEntity(formData.id, formData);
        setUsers(prevData =>
          prevData.map(user =>
            user.id === formData.id ? formData : user
          )
        );
        setNotification({ visible: true, title: "User Edited", message: "1 User has been edited" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      } else {
        const newUser = await usersApi.addEntity(formData);
        setUsers(prevData => [...prevData, newUser]);
        setNotification({ visible: true, title: "User Added", message: "1 User has been added" });
        setTimeout(() => {
          setNotification({ visible: false, title: "", message: "" });
        }, 2000);
      }
    } catch (err) {
      setErrorMessage({ open: true, title: currentForm.mode === "edit" ? "Error Updating User" : "Error Adding User", message: err.message });
    } finally {
      setCurrentForm(null);
    }
  }

  function handleCancelFormModal() {
    setCurrentForm(null);
  }

  function openDeleteModal(user) {
    setDeleteData({ open: true, userId: user.id });
  }

  async function confirmDelete() {
    try {
      await usersApi.deleteEntity(deleteData.userId);
      setUsers((prevUsers) =>
        prevUsers.filter((user) => user.id !== deleteData.userId)
      );
      setNotification({ visible: true, title: "User Deleted", message: "1 User has been deleted" });
      setTimeout(() => {
        setNotification({ visible: false, title: "", message: "" });
      }, 2000);
    } catch (err) {
      setErrorMessage({ open: true, title: "Error Deleting User", message: err.message })
    } finally {
      setDeleteData({ open: false, userId: null });
    }
  }

  function cancelDelete() {
    setDeleteData({ open: false, userId: null });
  };

  function dismissInformationalModal() {
    setErrorMessage({ open: false, title: "", message: "" })
  }

  if (isLoading) {
    return <Loading />;
  }

  const displayError = error
    ? error.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ))
    : "Ooops! No users found";

  return (
    <>
      {users.length === 0 && (
        <div className="d-flex flex-column align-items-center">
          <WarningAlert message={displayError} />
          <br />
          <div className="d-flex justify-content-center">
            <Button
              color="primary"
              onClick={(e) => {
                e.currentTarget.blur();
                setError(null);
                handleAdd();
              }}
              aria-label={error ? "Create First User" : "Create User"}
            >
              Create
            </Button>
          </div>
        </div>
      )}

      {users.length > 0 && (
        <div className="mb-5">
          <div className="d-flex justify-content-end align-items-center mb-2">
            <div style={{ marginRight: "1rem" }}>
              <Button
                color="transparent"
                onClick={(e) => {
                  e.currentTarget.blur();
                  handleAdd();
                }}
                aria-label="Add User"
              >
                <FontAwesomeIcon icon="plus" />
              </Button>
            </div>
            <div>
              <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />
            </div>
          </div>

          {filteredUsers.length > 0 ? (
            <>
              <Table bordered hover responsive size="sm" striped>
                <thead>
                  <tr>
                    <SortableHeaderCell columnKey={"customer_id"} label={"Customer_id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"idp_id"} label={"Idp_id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"id"} label={"Id"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"email"} label={"Email"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"language"} label={"Language"} currentSort={sortConfig} onSort={requestSort} />
                    <SortableHeaderCell columnKey={"timezone"} label={"Timezone"} currentSort={sortConfig} onSort={requestSort} />
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.map((user) => (
                    <EntityRow
                      key={user.id}
                      entity={user}
                      fields={["customer_id", "idp_id", "id", "email", "language", "timezone"]}
                      onEdit={handleEdit}
                      onDelete={openDeleteModal}
                      isSelected={
                        (currentForm && currentForm.mode === "edit" && user.id === currentForm.data.id) ||
                        user.id === deleteData.userId
                      }
                      renderField={(field, value) => {
                        if (field === "customer_id") {
                          return <Link to={`/customerDetails/${value}`}>{value.substring(0, 4)}</Link>;
                        }
                        if (field === "timezone") {
                            const tz = timezoneOptions.find((opt) => opt.value === value);
                            return tz ? tz.label : value;
                        }

                        return value;
                      }}
                    />
                  ))}
                </tbody>
              </Table>
              <div className="d-flex justify-content-between align-items-center">
                <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                <ItemsPerPageSelector
                  items={itemsPerPage}
                  currentSelection={usersPerPage}
                  onSelect={handleDropdownChange}
                  isOpen={dropdownOpen}
                  toggle={toggleDropdown}
                />
              </div>
            </>
          ) : (
            <WarningAlert message="Ooops! No users found" />
          )}
        </div>
      )}

      <InformationalModal
        isOpen={errorMessage.open} title={errorMessage.title} message={errorMessage.message}
        onCancel={dismissInformationalModal}
      />

      <ConfirmationModal
        isOpen={deleteData.open} title="Delete User?" message="Are you sure you want to delete this user?"
        onConfirm={confirmDelete} onCancel={cancelDelete} confirmLabel="OK" cancelLabel="Cancel"
      />

      <SimpleFormModal
        isOpen={!!currentForm} title={currentForm ? (currentForm.mode === "edit" ? "Edit User" : "Add User") : ""}
        initialData={currentForm ? currentForm.data : null} fields={userFields()} onSave={handleSaveForm} onCancel={handleCancelFormModal}
        defaultValues={userDefaultValues}
      />

      {notification.visible && (
        <NotificationToast icon="exclamation-triangle" title={notification.title} message={notification.message} fade={true} />
      )}
    </>
  );
};

export default withAuthenticationRequired(UsersComponent, {
  onRedirecting: () => <Loading />,
});
